// Master Apps · autosave de rascunho de formulário em localStorage.
// Substitui o botão "Salvar rascunho": grava sozinho, com debounce, a partir da
// PRIMEIRA alteração do usuário — o estado inicial intocado nunca é persistido.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Versão do envelope gravado · muda se o formato do rascunho mudar. */
const DRAFT_VERSION = 1;
/** Rascunhos mais antigos que isso são descartados na leitura (7 dias). */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Debounce padrão do autosave. */
const DEFAULT_DEBOUNCE_MS = 800;

export type FormDraftStatus = 'idle' | 'saving' | 'saved';

export interface FormDraft<T> {
  /** Rascunho encontrado no storage na montagem (null se não havia). Não é aplicado automaticamente. */
  restored: T | null;
  /** Momento do último autosave (null se ainda não salvou nesta sessão). */
  savedAt: Date | null;
  /** 'idle' | 'saving' | 'saved' — para indicador na UI. */
  status: FormDraftStatus;
  /** Descarta o rascunho do storage (usar ao publicar com sucesso ou quando o usuário recusa restaurar). */
  clear: () => void;
  /** Marca o rascunho restaurado como consumido, para o banner não reaparecer. */
  dismissRestored: () => void;
}

export interface FormDraftOptions<T> {
  /** ms de debounce do autosave. Default 800. */
  debounceMs?: number;
  /** Quando true, o autosave é suspenso (ex.: durante o publish). Default false. */
  paused?: boolean;
  /** Valor inicial "vazio" para comparação: enquanto value for igual a este, não salva. */
  pristine?: T;
}

// ─── Storage · toda operação é best-effort ────
// Modo privado, quota estourada ou SSR podem lançar / não ter localStorage:
// nada aqui pode derrubar a tela.

function hasStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

// JSON.stringify devolve undefined para undefined/função/symbol e lança em ciclos.
function safeStringify(value: unknown): string | undefined {
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

function readDraft<T>(key: string): T | null {
  if (!hasStorage()) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v?: unknown; savedAt?: unknown; data?: unknown } | null;
    // `parsed?.v` cobre os dois descartes de uma vez: `null` (JSON válido, mas
    // "null") vira `undefined`, que nunca é igual ao número da versão.
    if (parsed?.v !== DRAFT_VERSION) return null;
    if (typeof parsed.data !== 'object' || parsed.data === null) return null;
    // Sem data válida não é possível medir a idade — trata como rascunho suspeito.
    const at = typeof parsed.savedAt === 'string' ? Date.parse(parsed.savedAt) : Number.NaN;
    if (!Number.isFinite(at) || Date.now() - at > MAX_AGE_MS) return null;
    return parsed.data as T;
  } catch {
    // JSON quebrado ou storage bloqueado: segue sem rascunho.
    return null;
  }
}

// `dataJson` já é JSON válido (o mesmo texto usado na comparação estrutural),
// então o envelope é montado por concatenação, sem re-serializar o formulário.
function writeDraft(key: string, dataJson: string, at: Date): boolean {
  if (!hasStorage()) return false;
  try {
    const envelope = `{"v":${DRAFT_VERSION},"savedAt":${JSON.stringify(at.toISOString())},"data":${dataJson}}`;
    localStorage.setItem(key, envelope);
    return true;
  } catch {
    return false;
  }
}

function removeDraft(key: string): void {
  if (!hasStorage()) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignora
  }
}

// ─── Hook ─────────────────────────────────────

export function useFormDraft<T>(
  key: string,
  value: T,
  options?: FormDraftOptions<T>,
): FormDraft<T> {
  const debounceMs = options?.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const paused = options?.paused ?? false;

  // Comparação estrutural via JSON: o estado do formulário é pequeno e plano.
  // Serializar também estabiliza as deps do efeito (objeto novo com mesmo
  // conteúdo não reagenda o autosave).
  const serialized = safeStringify(value);
  const pristineSerialized =
    options?.pristine === undefined ? undefined : safeStringify(options.pristine);

  // Rascunho lido UMA vez, na montagem. Quem consome decide se aplica.
  const [restored, setRestored] = useState<T | null>(() => readDraft<T>(key));
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [status, setStatus] = useState<FormDraftStatus>('idle');

  // Valor do 1º render: enquanto o formulário for igual a ele, não houve alteração.
  const initialRef = useRef(serialized);
  const firstRunRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const serializedRef = useRef(serialized);
  // Último valor efetivamente gravado — evita regravar/reexibir "salvando".
  const savedSerializedRef = useRef<string | null>(null);
  // Valor congelado por clear(): impede que o efeito regrave o mesmo rascunho.
  const cancelledRef = useRef<string | null>(null);

  useEffect(() => {
    serializedRef.current = serialized;

    // A montagem nunca grava: aqui o valor ainda é o inicial, intocado.
    if (firstRunRef.current) {
      firstRunRef.current = false;
      return;
    }

    // Nada serializável (undefined / ciclo): não há rascunho a gravar.
    if (serialized === undefined) return;

    // Igual ao 1º render: o usuário não mexeu em nada.
    if (serialized === initialRef.current) return;

    // Voltou ao estado vazio: descarta em vez de gravar um rascunho inútil.
    if (pristineSerialized !== undefined && serialized === pristineSerialized) {
      removeDraft(key);
      savedSerializedRef.current = null;
      setSavedAt(null);
      setStatus('idle');
      return;
    }

    // clear() já descartou exatamente este valor (ex.: publish concluído).
    if (serialized === cancelledRef.current) return;

    // Este valor já está no storage: nada pendente.
    if (serialized === savedSerializedRef.current) return;

    // Pausado (ex.: durante o publish): mantém pendente e espera o despause —
    // `paused` é dep do efeito, então a gravação acontece ao voltar a false.
    if (paused) {
      setStatus('saving');
      return;
    }

    setStatus('saving');
    const timer = setTimeout(() => {
      timerRef.current = null;
      const at = new Date();
      if (writeDraft(key, serialized, at)) {
        savedSerializedRef.current = serialized;
        setSavedAt(at);
        setStatus('saved');
      } else {
        // Storage indisponível: falha silenciosa, sem indicador enganoso.
        setStatus('idle');
      }
    }, debounceMs);
    timerRef.current = timer;

    return () => {
      clearTimeout(timer);
      if (timerRef.current === timer) timerRef.current = null;
    };
  }, [key, serialized, pristineSerialized, paused, debounceMs]);

  const clear = useCallback(() => {
    // Cancela o autosave pendente ANTES de apagar: sem isso o timeout já
    // agendado ressuscita o rascunho segundos depois do publish.
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    cancelledRef.current = serializedRef.current ?? null;
    savedSerializedRef.current = null;
    removeDraft(key);
    setRestored(null);
    setSavedAt(null);
    setStatus('idle');
  }, [key]);

  const dismissRestored = useCallback(() => setRestored(null), []);

  return useMemo(
    () => ({ restored, savedAt, status, clear, dismissRestored }),
    [restored, savedAt, status, clear, dismissRestored],
  );
}
